/**
 * Plan & billing (#44/#45): trial, subscription, seats and storage usage. Owners subscribe
 * (Stripe Checkout) and manage payment details, invoices, monthly/yearly and cancellation
 * in Stripe's customer portal.
 */
import { useState } from "react";
import { Alert, Badge, Button, Card, Col, ProgressBar, Row } from "react-bootstrap";
import { useSearchParams } from "react-router";
import { QueryState, Section } from "../../components/ui/kit.tsx";
import { api } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { formatBytes, useFormat } from "../../lib/format.ts";
import { useApi } from "../../lib/query.ts";

export interface BillingSummary {
  billingEnabled: boolean;
  exempt: boolean;
  plan: string;
  planLabel: string;
  prices: { monthPence: number; yearPence: number };
  interval: "month" | "year" | null;
  status: string;
  access: { state: "full" | "grace" | "read_only" | "suspended"; reason: string };
  trialEndsAt: string | null;
  trialDaysLeft: number | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  subscribed: boolean;
  seats: number;
  storage: { usedBytes: number; allowanceBytes: number | null; perSeatGb: number; extraGb: number };
}

const pounds = (pence: number) => `£${(pence / 100).toFixed(2)}`;

export function BillingPage() {
  const { me } = useAuth();
  const f = useFormat();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const q = useApi<BillingSummary>("/api/billing");
  const isOwner = me?.teams.find((t) => t.id === me.activeTeamId)?.isOwner ?? false;

  async function go(path: string, body: unknown, key: string) {
    setBusy(key);
    setError(null);
    try {
      const { url } = await api.post<{ url: string }>(path, body);
      window.location.assign(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  }

  return (
    <QueryState query={q}>
      {(b) => {
        const storagePct = b.storage.allowanceBytes
          ? Math.min(100, Math.round((b.storage.usedBytes / b.storage.allowanceBytes) * 100))
          : 0;
        return (
          <>
            {params.get("checkout") === "success" && (
              <Alert variant="success">
                Thank you! Your subscription is being set up; this page updates in a moment.
              </Alert>
            )}
            {error && <Alert variant="danger">{error}</Alert>}
            <Section title="Your plan">
              {!b.billingEnabled || b.exempt ? (
                <p className="mb-0">This team isn't billed.</p>
              ) : (
                <>
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <strong>{b.planLabel}</strong>
                    <StatusBadge summary={b} />
                  </div>
                  {b.access.reason === "trial" && b.trialDaysLeft !== null && !b.subscribed && (
                    <p>
                      Free trial: <strong>{b.trialDaysLeft} days left</strong> (ends {f.date(b.trialEndsAt)}).
                      No card needed until you choose a plan; your trial carries on until then.
                    </p>
                  )}
                  {b.access.state === "read_only" && (
                    <Alert variant="warning">
                      {b.access.reason === "trial_ended"
                        ? "Your free trial has ended, so the team is read-only. Your records are safe: choose a plan to carry on making changes."
                        : "The team is read-only until the subscription is paid. Your records are safe and you can still export them."}
                    </Alert>
                  )}
                  {b.access.state === "grace" && (
                    <Alert variant="warning">
                      A payment didn't go through. Please update your card to avoid the team becoming
                      read-only.
                    </Alert>
                  )}
                  {b.subscribed && (
                    <p className="mb-2">
                      Billed {b.interval === "year" ? "yearly" : "monthly"} for {b.seats} member
                      {b.seats === 1 ? "" : "s"}.{" "}
                      {b.currentPeriodEnd &&
                        (b.cancelAtPeriodEnd
                          ? `Ends on ${f.date(b.currentPeriodEnd)}.`
                          : `Renews on ${f.date(b.currentPeriodEnd)}.`)}
                    </p>
                  )}
                  {isOwner ? (
                    b.subscribed ? (
                      <Button
                        variant="outline-primary"
                        disabled={busy !== null}
                        onClick={() => go("/api/billing/portal", {}, "portal")}
                      >
                        {busy === "portal" ? "Opening…" : "Manage billing, invoices and card"}
                      </Button>
                    ) : (
                      <Row className="g-3 mt-1">
                        <Col md={6}>
                          <PlanCard
                            title="Monthly"
                            price={`${pounds(b.prices.monthPence)} per member per month`}
                            total={`${pounds(b.prices.monthPence * b.seats)} a month for your ${b.seats} member${b.seats === 1 ? "" : "s"}`}
                            busy={busy === "month"}
                            disabled={busy !== null}
                            onClick={() => go("/api/billing/checkout", { interval: "month" }, "month")}
                          />
                        </Col>
                        <Col md={6}>
                          <PlanCard
                            title="Yearly"
                            badge="2 months free"
                            price={`${pounds(b.prices.yearPence)} per member per year`}
                            total={`${pounds(b.prices.yearPence * b.seats)} a year for your ${b.seats} member${b.seats === 1 ? "" : "s"}`}
                            busy={busy === "year"}
                            disabled={busy !== null}
                            onClick={() => go("/api/billing/checkout", { interval: "year" }, "year")}
                          />
                        </Col>
                      </Row>
                    )
                  ) : (
                    <p className="small text-muted mb-0">
                      Only an Owner of the team can change the plan or payment details.
                    </p>
                  )}
                  <p className="small text-muted mt-3 mb-0">
                    Prices include VAT where it applies. Each member of the team is a seat; adding or removing
                    a member changes your bill from that day, pro rata. We never delete your records if a
                    payment fails.
                  </p>
                </>
              )}
            </Section>
            <Section title="Usage">
              <dl className="row mb-0">
                <dt className="col-sm-4 fw-normal">Members (seats)</dt>
                <dd className="col-sm-8">{b.seats}</dd>
                <dt className="col-sm-4 fw-normal">Document storage</dt>
                <dd className="col-sm-8">
                  {b.storage.allowanceBytes ? (
                    <>
                      {formatBytes(b.storage.usedBytes)} of {formatBytes(b.storage.allowanceBytes)} used
                      <ProgressBar
                        now={storagePct}
                        variant={storagePct >= 100 ? "danger" : storagePct >= 80 ? "warning" : "primary"}
                        className="mt-1"
                        style={{ height: 8 }}
                        aria-label="Storage used"
                      />
                      <div className="small text-muted mt-1">
                        {b.storage.perSeatGb} GB per member
                        {b.storage.extraGb ? `, plus ${b.storage.extraGb} GB extra` : ""}. Deleted documents
                        don't count.
                      </div>
                    </>
                  ) : (
                    `${formatBytes(b.storage.usedBytes)} used`
                  )}
                </dd>
              </dl>
            </Section>
          </>
        );
      }}
    </QueryState>
  );
}

function StatusBadge({ summary: b }: { summary: BillingSummary }) {
  if (b.access.state === "suspended") return <Badge bg="danger">Suspended</Badge>;
  if (b.access.state === "read_only")
    return (
      <Badge bg="warning" text="dark">
        Read-only
      </Badge>
    );
  if (b.access.state === "grace")
    return (
      <Badge bg="warning" text="dark">
        Payment failed
      </Badge>
    );
  if (b.access.reason === "trial") return <Badge bg="info">Free trial</Badge>;
  return <Badge bg="success">Active</Badge>;
}

function PlanCard(props: {
  title: string;
  price: string;
  total: string;
  badge?: string;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Card className="h-100">
      <Card.Body className="d-flex flex-column">
        <div className="d-flex align-items-center gap-2 mb-1">
          <strong>{props.title}</strong>
          {props.badge && <Badge bg="success">{props.badge}</Badge>}
        </div>
        <div>{props.price}</div>
        <div className="small text-muted mb-3">{props.total}</div>
        <Button className="mt-auto" disabled={props.disabled} onClick={props.onClick}>
          {props.busy ? "Opening checkout…" : `Choose ${props.title.toLowerCase()}`}
        </Button>
      </Card.Body>
    </Card>
  );
}

/** Banner above every page: trial ending, payment failed, read-only. Suspended teams see only this. */
export function BillingBanner() {
  const { me } = useAuth();
  const b = me?.billing;
  if (!b || b.state === "full") {
    if (b?.reason === "trial" && b.trialDaysLeft !== null && b.trialDaysLeft <= 14)
      return (
        <Alert variant="info" className="rounded-0 mb-0 py-2 small no-print">
          Your free trial ends in {b.trialDaysLeft} day{b.trialDaysLeft === 1 ? "" : "s"}.{" "}
          <Alert.Link href="/settings/billing">Choose a plan</Alert.Link> to keep making changes.
        </Alert>
      );
    return null;
  }
  const text =
    b.state === "grace"
      ? "A payment didn't go through. Please update your card to keep full access."
      : b.state === "read_only"
        ? b.reason === "trial_ended"
          ? "Your free trial has ended. Your records are read-only until you choose a plan."
          : "This team is read-only until the subscription is paid."
        : "This team is suspended because payment is overdue.";
  return (
    <Alert
      variant={b.state === "suspended" ? "danger" : "warning"}
      className="rounded-0 mb-0 py-2 small no-print"
    >
      {text} <Alert.Link href="/settings/billing">Plan &amp; billing</Alert.Link>
    </Alert>
  );
}
